// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect, useMemo, type ReactNode, useState } from 'react';

import type { PartCatalog, PartListPart, PartAttributeDefinitionVariantId } from '@evolvemep/api-ts';

import type { UseState } from 'helpers/helper-types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useLocalStorage } from 'hooks/useLocalStorage';
import type { NeedsRefreshItem } from 'modules/Shop/CatalogSetup/CatalogIntrospector';

type CatalogSetupContextType = {
  selectedCatalog: PartCatalog | null;
  onCatalogSelected: (catalog: PartCatalog | null) => void;
  needsRefresh: NeedsRefreshItem[];
  forceRefresh: (...items: NeedsRefreshItem[]) => void;
  selectedPart: PartListPart | null;
  onPartSelected: (part: PartListPart | null, navigateToCategory?: boolean) => void;
  editing: boolean;
  onEditingChanged: UseState<boolean>;
  partFilter: PartAttributeDefinitionVariantId[];
  onPartFilterChanged: UseState<PartAttributeDefinitionVariantId[]>;
};

const CatalogSetupContext = React.createContext<CatalogSetupContextType | undefined>(undefined);

export const CatalogSetupProvider = ({ children }: { children: ReactNode }) => {
  const [selectedCatalog, setSelectedCatalog] = useLocalStorage('SELECTED_CATALOG', null);

  const [selectedPart, setSelectedPart] = useState<PartListPart | null>(null);
  const [editing, setEditing] = useState(false);
  const [partFilter, setPartFilter] = useState<PartAttributeDefinitionVariantId[]>([]);

  /**
   * Used to force a re-render of downstream components,
   * typically to force a re-fetch of their backing data.
   * This is a hacky solution.
   */
  const [needsRefresh, setNeedsRefresh] = useState<NeedsRefreshItem[]>([]);
  const forceRefresh = useCallback((...items: NeedsRefreshItem[]) => {
    setNeedsRefresh((n) => [...n, ...items]);
  }, []);
  useEffect(() => {
    if (needsRefresh.length > 0) setNeedsRefresh([]);
  }, [needsRefresh.length]);

  const onCatalogSelected = useCallback<typeof setSelectedCatalog>(
    (catalog) => {
      setSelectedCatalog(catalog);
      setSelectedPart(null);
      setPartFilter([]);
    },
    [setSelectedCatalog],
  );

  const value = useMemo<CatalogSetupContextType>(
    () => ({
      onCatalogSelected,
      selectedCatalog,
      needsRefresh,
      forceRefresh,
      editing,
      onEditingChanged: setEditing,
      partFilter,
      onPartFilterChanged: setPartFilter,
      selectedPart,
      onPartSelected: setSelectedPart,
    }),
    [onCatalogSelected, selectedCatalog, needsRefresh, forceRefresh, editing, partFilter, selectedPart],
  );

  return <CatalogSetupContext value={value}>{children}</CatalogSetupContext>;
};

export const useCatalogSetup = () => useGeneralContext(CatalogSetupContext, 'CatalogSetup');
